import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppContextService } from '../../../services/app-context.service';
import { LoginApiService } from '../../../server-api/login-api.service';
import { finalize } from 'rxjs';
import { OperationResult } from '../../../models/server-result/operation-result';
import { GetTokenResult } from '../../../models/User/auth/get-token-result';
import { LoginService } from '../../../services/authentication/login.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LogService } from '../../../services/log/log.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { TranslateService } from '../../../services/translate/translate.service';

@Component({
	selector: 'app-user-login-role-selection',
	templateUrl: './user-login-role-selection.component.html',
	styleUrls: ['./user-login-role-selection.component.scss'],
	standalone: false,
})
export class UserLoginRoleSelectionComponent implements OnInit {
	protected username: string;
	protected profiles: string[];
	private returnUrl: string | null;
	private authCode: string;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private appContext: AppContextService,
		private loginApiService: LoginApiService,
		private loginService: LoginService,
		private logger: LogService,
		private toasterService: ToasterService,
		private translateService: TranslateService
	) {}

	ngOnInit(): void {
		this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || null;
		this.authCode = this.route.snapshot.queryParams['ac'] || '';

		if (!!this.authCode) {
			let userPendingProfileSelection = this.appContext.getUserPendingProfileSelection(this.authCode);
			if (!!userPendingProfileSelection) {
				this.username = userPendingProfileSelection.Username;
				this.profiles = userPendingProfileSelection.Profiles;
			} else {
				this.logout();
			}
		} else {
			this.logout();
		}
	}

	public handleRoleSelect(selectedProfile: string): void {
		if (!!this.username) {
			this.loginApiService.getJwtTokenByAuthCode(this.authCode, selectedProfile).subscribe({
				next: (operationResult: OperationResult<GetTokenResult>) => {
					if (operationResult.IsValid && !!this.username) {
						this.appContext.removeUserPendingProfileSelection();
						this.appContext.storeUserProfileOnLogin(this.username, operationResult.Data);
						this.loginService.getUserInfoOnLoginSuccess(this.username, operationResult.Data, this.returnUrl);
					} else {
						this.logout();
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.logout();
					this.processLoginCallError(httpErrorResponse);
				},
			});
		}
	}

	public logout() {
		console.log('logout()');
		if (!!this.username && !!this.appContext.getToken()) {
			this.loginApiService
				.invalidateToken()
				.pipe(
					finalize(() => {
						// reset app context and go to login page
						this.appContext.removeSession();
						this.router.navigate(['public/login']);
					})
				)
				.subscribe();
		} else {
			this.appContext.removeSession();
			this.router.navigate(['public/login']);
		}
	}

	private processLoginCallError(httpErrorResponse: HttpErrorResponse) {
		if (httpErrorResponse.error instanceof HttpErrorResponse) {
			if (httpErrorResponse.error.status !== 403) {
				//403 is handled by http-error.interceptor
				this.logger.error('[LoginComponent::login] HttpErrorResponse: ', httpErrorResponse);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse, httpErrorResponse.message);
				return;
			}
		}
		this.logger.log(httpErrorResponse.message);
		this.logger.warn('warning: ' + httpErrorResponse.message);
	}

	public getProfileTitleAndDescription(profile: string): { title: string; description: string } {
		const profileDetails = {
			title: this.translateService.translate(profile, 'list-role'),
			description: '',
		};

		switch (profile) {
			case 'Buyer':
				profileDetails.description = this.translateService.translate('userLogin.buyer-description', 'client');
				break;
			case 'Supplier':
				profileDetails.description = this.translateService.translate('userLogin.supplier-description', 'client');
				break;
			case 'Financier':
				profileDetails.description = this.translateService.translate('userLogin.financier-description', 'client');
				break;
			case 'SystemAdmin':
				profileDetails.description = this.translateService.translate('userLogin.systemadmin-description', 'client');
				break;
		}

		return profileDetails;
	}
}
